/**
 * HighlightsEditor
 * Allows managing an array of highlight strings for vehicle booking display
 */
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, X, GripVertical } from 'lucide-react'

interface HighlightsEditorProps {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
}

export function HighlightsEditor({ value, onChange, disabled }: HighlightsEditorProps) {
  const [newHighlight, setNewHighlight] = useState('')

  const addHighlight = () => {
    const trimmed = newHighlight.trim()
    if (trimmed) {
      onChange([...value, trimmed])
      setNewHighlight('')
    }
  }

  const removeHighlight = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const updateHighlight = (index: number, text: string) => {
    const updated = [...value]
    updated[index] = text
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      {value.map((highlight, index) => (
        <div key={index} className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input
            value={highlight}
            onChange={(e) => updateHighlight(index, e.target.value)}
            disabled={disabled}
            className="flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeHighlight(index)}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Input
          value={newHighlight}
          onChange={(e) => setNewHighlight(e.target.value)}
          placeholder="Add a highlight..."
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addHighlight()
            }
          }}
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addHighlight}
          disabled={disabled || !newHighlight.trim()}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>
    </div>
  )
}
